import {
  makeStyles,
  mergeClasses,
  shorthands,
  useButtonStyles_unstable,
} from "@fluentui/react-components";
import type { AppPaddleButtonState } from "./AppPaddleButton.types";
import { customTokens } from "@1js/acui-theme";
import { PaddleOrientation } from "..";

// eslint-disable-next-line no-restricted-imports
import {
  commonHighContrastHoverBorderStyles,
  HighContrastSelector,
} from "@1js/acui-common/lib/styling/next/commonStyles.styles";

const useAppPaddleButtonBaseStyles = makeStyles({
  base: {
    position: "absolute",
    height: "100%",
    minWidth: "24px",
    maxWidth: "24px",
    backgroundColor: customTokens.colorNeutralBackground1,
    ...shorthands.padding("0px"),
    ...shorthands.borderRadius("0px"),
    ...shorthands.border("1px", "solid", customTokens.colorNeutralStroke1),
    ":hover": {
      backgroundColor: customTokens.colorNeutralBackground1Hover,
      ...shorthands.borderColor(customTokens.colorNeutralStroke1Hover),
      [`${HighContrastSelector}`]: {
        ...commonHighContrastHoverBorderStyles,
        backgroundColor: "HighlightText",
        color: "Highlight",
        "& path": {
          fill: `Highlight !important`,
        },
      },
    },
    ":hover:active": {
      backgroundColor: customTokens.colorNeutralBackground1Pressed,
      ...shorthands.borderColor(customTokens.colorNeutralStroke1Pressed),
      [`${HighContrastSelector}`]: {
        ...commonHighContrastHoverBorderStyles,
        backgroundColor: "HighlightText",
        color: "Highlight",
        "& path": {
          fill: `Highlight !important`,
        },
      },
    },
  },
  paddleLeft: {
    left: "0px",
    ...shorthands.borderRadius(
      customTokens.borderRadiusXLarge,
      "0px",
      "0px",
      customTokens.borderRadiusXLarge
    ),
  },
  paddleRight: {
    right: 0,
    ...shorthands.borderRadius(
      "0px",
      customTokens.borderRadiusXLarge,
      customTokens.borderRadiusXLarge,
      "0px"
    ),
  },
  paddleRightWithPadding: {
    right: "32px",
  },
  paddleZIndex: {
    zIndex: 100,
  },
});

const useIconStyles = makeStyles({
  root: {
    height: "12px",
    width: "12px",
    fontSize: "12px",
    fill: customTokens.colorNeutralForeground1,
  },
  rotateLeft: {
    transform: "rotate(90deg)",
  },
  rotateRight: {
    transform: "rotate(270deg)",
  },
});

export const useAppPaddleButtonStyles = (state: AppPaddleButtonState) => {
  const baseStyles = useAppPaddleButtonBaseStyles();
  const baseIconStyles = useIconStyles();

  const isLeftPaddle = state.orientation === PaddleOrientation.Left;

  state.root.className = mergeClasses(
    baseStyles.base,
    baseStyles.paddleZIndex,
    state.orientation === PaddleOrientation.Left && baseStyles.paddleLeft,
    state.orientation === PaddleOrientation.Right &&
      state.disableModeTogglePadding &&
      baseStyles.paddleRight,
    state.orientation === PaddleOrientation.Right &&
      !state.disableModeTogglePadding &&
      baseStyles.paddleRightWithPadding,
    state.root.className
  );

  if (state.icon) {
    state.icon.className = mergeClasses(
      baseIconStyles.root,
      isLeftPaddle && baseIconStyles.rotateLeft,
      !isLeftPaddle && baseIconStyles.rotateRight,
      state.icon.className
    );
  }

  useButtonStyles_unstable(state);

  return state;
};
